/**
 * Маскот квокка — рендеры из `pictures/` (алиас `@pictures`).
 *
 * Двигается тело, а не картинка: опора — лапы (`transform-origin` снизу),
 * поэтому квокка дышит, покачивается, приседает перед прыжком и
 * приземляется со сжатием; тень под ней отвечает на прыжок. Смена
 * настроения — перекрёстное появление поз с пружиной.
 */

import { AnimatePresence, motion, useReducedMotion, type TargetAndTransition } from 'framer-motion';

import celebrate from '@pictures/happy.png';
import idle from '@pictures/cute.png';
import error from '@pictures/shocked.png';
import welcome from '@pictures/hello.png';
import thinking from '@pictures/think.png';

/** Настроение квокки; каждому соответствует своя поза и движение. */
export type QuokkaMood = 'welcome' | 'thinking' | 'celebrate' | 'error' | 'idle';

const SOURCES: Record<QuokkaMood, string> = { welcome, thinking, celebrate, error, idle };

/** Размер маскота — от значка в уведомлении до фигуры на весь экран. */
export type QuokkaSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const SIZE_CLASS: Record<QuokkaSize, string> = {
  xs: 'h-10 w-10',
  sm: 'h-14 w-14',
  md: 'h-24 w-24 lg:h-28 lg:w-28',
  lg: 'h-40 w-40 lg:h-48 lg:w-48 2xl:h-56 2xl:w-56',
  xl: 'h-60 w-60 lg:h-72 lg:w-72 2xl:h-80 2xl:w-80',
  '2xl': 'h-80 w-80 lg:h-[26rem] lg:w-[26rem] 2xl:h-[30rem] 2xl:w-[30rem]',
};

const loop = (duration: number) => ({ duration, repeat: Infinity, ease: 'easeInOut' as const });
const HOP = { duration: 1, repeat: Infinity, repeatDelay: 0.35, times: [0, 0.18, 0.48, 0.72, 0.86, 1] };

const BODY: Record<QuokkaMood, TargetAndTransition> = {
  idle: { scaleY: [1, 1.025, 1], scaleX: [1, 0.99, 1], transition: loop(3.2) },
  welcome: { rotate: [-2.5, 2.5, -2.5], y: [0, -3, 0], transition: loop(2.2) },
  thinking: { rotate: [-3, 1, -3], y: [0, -2, 0], transition: loop(3.6) },
  celebrate: {
    y: [0, 4, -28, 0, -6, 0],
    scaleY: [1, 0.86, 1.08, 0.9, 1.03, 1],
    scaleX: [1, 1.1, 0.95, 1.08, 0.99, 1],
    transition: HOP,
  },
  error: {
    x: [0, -5, 5, -3, 0],
    scaleY: [1, 0.94, 1.02, 1],
    transition: { duration: 0.55, repeat: Infinity, repeatDelay: 1.8 },
  },
};

const SHADOW: Partial<Record<QuokkaMood, TargetAndTransition>> = {
  celebrate: { scaleX: [1, 1.08, 0.6, 1.08, 0.9, 1], opacity: [0.22, 0.26, 0.1, 0.26, 0.2, 0.22], transition: HOP },
};

interface QuokkaProps {
  readonly mood: QuokkaMood;
  /** Осмысленная альтернатива; пустая строка делает картинку декоративной. */
  readonly alt: string;
  readonly size?: QuokkaSize;
  readonly className?: string;
}

export function Quokka({ mood, alt, size = 'md', className }: QuokkaProps) {
  const still = useReducedMotion() === true;

  return (
    <motion.span
      initial={{ scale: 0.7, opacity: 0, y: 14 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      whileHover={still ? undefined : { y: [0, -10, 0], scaleY: [1, 1.05, 1], transition: { duration: 0.45 } }}
      transition={{ type: 'spring', stiffness: 320, damping: 20 }}
      style={{ transformOrigin: '50% 100%' }}
      className={['relative inline-flex shrink-0 items-end justify-center', SIZE_CLASS[size], className ?? ''].join(' ')}
    >
      <motion.span
        aria-hidden="true"
        className="absolute bottom-[2%] left-1/2 h-[7%] w-[60%] -translate-x-1/2 rounded-[50%] bg-heading/20 blur-[3px]"
        style={{ opacity: 0.22 }}
        animate={still ? undefined : SHADOW[mood]}
      />
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={mood}
          className="absolute inset-0 flex items-end justify-center"
          style={{ transformOrigin: '50% 100%' }}
          initial={{ opacity: 0, scale: 0.9, y: 6 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.15 } }}
          transition={{ type: 'spring', stiffness: 360, damping: 22 }}
        >
          <motion.img
            src={SOURCES[mood]}
            alt={alt}
            draggable={false}
            className="h-full w-full select-none object-contain object-bottom drop-shadow-[0_6px_10px_rgb(28_26_58/0.18)] dark:drop-shadow-[0_6px_14px_rgb(0_0_0/0.55)]"
            style={{ transformOrigin: '50% 100%' }}
            animate={still ? undefined : BODY[mood]}
          />
        </motion.span>
      </AnimatePresence>
    </motion.span>
  );
}
